import React from 'react';
import { AlertCircle } from 'lucide-react';
import { CAMPAIGN_TIMEZONES, CAMPAIGN_DAYS_OF_WEEK } from '../../../../constants/scheduling';
import type { SettingsStepProps } from '../types';

/**
 * Settings step for campaign creation.
 * Configures email account, time window, days of week, and daily limit.
 */
export const SettingsStep: React.FC<SettingsStepProps> = ({
  campaignState,
  gmailConnections,
  estimatedDays,
  error,
  onUpdateState,
  onLaunch,
}) => {
  const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const activeConnections = gmailConnections.filter((c) => c.token_status === 'active');

  return (
    <div className="mt-3">
      {/* Settings card */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        {/* Email Account */}
        <div className="p-3 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <span className="text-[12px] font-medium text-gray-600">Send from</span>
            {activeConnections.length === 0 ? (
              <span className="text-[12px] text-amber-600">Connect Gmail in Settings</span>
            ) : (
              <select
                value={campaignState.emailAccountId || ''}
                onChange={(e) => {
                  const conn = activeConnections.find((c) => c.id === e.target.value);
                  onUpdateState({
                    emailAccountId: e.target.value,
                    emailAccount: conn,
                  });
                }}
                className="px-2 py-1.5 text-[12px] bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:border-gray-300"
              >
                {activeConnections.map((conn) => (
                  <option key={conn.id} value={conn.id}>
                    {conn.gmail_email}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>

        {/* Time window */}
        <div className="p-3 border-b border-gray-100">
          <div className="flex flex-col gap-2">
            <span className="text-[12px] font-medium text-gray-600">Time window</span>
            <div className="flex flex-wrap items-center gap-2">
              <select
                value={campaignState.startHour}
                onChange={(e) => onUpdateState({ startHour: parseInt(e.target.value) })}
                className="px-2 py-1.5 text-[12px] bg-gray-50 border border-gray-200 rounded-lg focus:outline-none min-w-[70px]"
              >
                {Array.from({ length: 24 }, (_, i) => (
                  <option key={i} value={i}>
                    {i === 0 ? '12am' : i < 12 ? `${i}am` : i === 12 ? '12pm' : `${i - 12}pm`}
                  </option>
                ))}
              </select>
              <span className="text-[11px] text-gray-400">to</span>
              <select
                value={campaignState.endHour}
                onChange={(e) => onUpdateState({ endHour: parseInt(e.target.value) })}
                className="px-2 py-1.5 text-[12px] bg-gray-50 border border-gray-200 rounded-lg focus:outline-none min-w-[70px]"
              >
                {Array.from({ length: 24 }, (_, i) => (
                  <option key={i} value={i}>
                    {i === 0 ? '12am' : i < 12 ? `${i}am` : i === 12 ? '12pm' : `${i - 12}pm`}
                  </option>
                ))}
              </select>
              <select
                value={campaignState.timezone}
                onChange={(e) => onUpdateState({ timezone: e.target.value })}
                className="px-2 py-1.5 text-[12px] bg-gray-50 border border-gray-200 rounded-lg focus:outline-none min-w-[100px]"
              >
                {CAMPAIGN_TIMEZONES.map((tz) => (
                  <option key={tz.value} value={tz.value}>
                    {tz.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Days of week */}
        <div className="p-3 border-b border-gray-100">
          <div className="flex flex-col gap-2">
            <span className="text-[12px] font-medium text-gray-600">Send on</span>
            <div className="flex flex-wrap gap-1.5">
              {CAMPAIGN_DAYS_OF_WEEK.map((day, index) => {
                const isSelected = campaignState.daysOfWeek.includes(day.value);
                return (
                  <button
                    key={`${day.value}-${index}`}
                    onClick={() => {
                      onUpdateState({
                        daysOfWeek: isSelected
                          ? campaignState.daysOfWeek.filter((d) => d !== day.value)
                          : [...campaignState.daysOfWeek, day.value],
                      });
                    }}
                    className={`min-w-[40px] px-2 h-8 text-[11px] font-medium rounded-lg transition-all ${
                      isSelected
                        ? 'bg-gray-900 text-white'
                        : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                    }`}
                  >
                    {dayNames[index]}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Daily limit */}
        <div className="p-3">
          <div className="flex items-center justify-between">
            <span className="text-[12px] font-medium text-gray-600">Daily limit</span>
            <div className="flex items-center gap-2">
              <input
                type="number"
                value={campaignState.dailyLimit}
                onChange={(e) => {
                  const parsed = parseInt(e.target.value) || 50;
                  const clamped = Math.min(Math.max(parsed, 1), 200);
                  onUpdateState({ dailyLimit: clamped });
                }}
                min={1}
                max={200}
                className="w-16 px-2 py-1.5 text-[12px] text-center bg-gray-50 border border-gray-200 rounded-lg focus:outline-none"
              />
              <span className="text-[11px] text-gray-400">/day</span>
            </div>
          </div>
        </div>
      </div>

      {/* Estimated completion */}
      <div className="mt-3 px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg">
        <p className="text-[12px] text-gray-600">
          With these settings, your campaign will take approximately{' '}
          <span className="font-medium text-gray-900">
            {estimatedDays} {estimatedDays === 1 ? 'day' : 'days'}
          </span>{' '}
          to complete.
        </p>
      </div>

      {error && (
        <div className="mt-3 flex items-center gap-1.5 p-2 bg-red-50 border border-red-100 rounded-lg">
          <AlertCircle className="w-3 h-3 text-red-500" />
          <span className="text-[11px] text-red-600">{error}</span>
        </div>
      )}

      {/* Actions */}
      <div className="mt-4">
        <button
          onClick={onLaunch}
          disabled={!campaignState.emailAccountId || campaignState.daysOfWeek.length === 0}
          className="px-4 py-2 text-[13px] font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          Review & Launch
        </button>
      </div>
    </div>
  );
};

SettingsStep.displayName = 'SettingsStep';
